import { RefObject, useLayoutEffect } from 'react';

import { deviceNumberSizes } from 'config/device';

const COMPACT_POPOVER_WIDTH = 320;
const MARGIN = 8;

const POINTER_STATE = '__portalDatePointers';

function pointerCount() {
  const state = (window as Window & { [POINTER_STATE]?: { active: number } })[
    POINTER_STATE
  ];
  return state?.active ?? 0;
}

function trackPointers() {
  const root = window as Window & { [POINTER_STATE]?: { active: number } };
  if (root[POINTER_STATE]) return;
  const state = { active: 0 };
  root[POINTER_STATE] = state;
  const down = () => {
    state.active += 1;
  };
  const up = () => {
    state.active = Math.max(0, state.active - 1);
  };
  window.addEventListener('pointerdown', down, true);
  window.addEventListener('pointerup', up, true);
  window.addEventListener('pointercancel', up, true);
}

if (typeof window !== 'undefined') trackPointers();

function viewport() {
  const visual = window.visualViewport;
  return {
    width: visual?.width ?? window.innerWidth,
    height: visual?.height ?? window.innerHeight,
    top: visual?.offsetTop ?? 0,
    left: visual?.offsetLeft ?? 0,
  };
}

function isCompactViewport() {
  return viewport().width <= deviceNumberSizes.tablet;
}

function placePopover(popover: HTMLElement, anchor: HTMLElement) {
  const rect = anchor.getBoundingClientRect();
  const view = viewport();
  const width = Math.min(COMPACT_POPOVER_WIDTH, view.width - MARGIN * 2);
  const available = view.height - MARGIN * 2;
  popover.style.maxHeight = `${available}px`;
  popover.style.overflowY = 'auto';
  const height = Math.min(popover.offsetHeight || 420, available);
  const spaceBelow = view.top + view.height - rect.bottom;
  const spaceAbove = rect.top - view.top;
  const openAbove = spaceBelow < height + MARGIN && spaceAbove > spaceBelow;
  let top = openAbove ? rect.top - height - MARGIN : rect.bottom + MARGIN;
  let left = rect.left;
  left = Math.min(
    Math.max(left, view.left + MARGIN),
    view.left + view.width - width - MARGIN,
  );
  const maxTop = Math.max(view.top + MARGIN, view.top + view.height - height - MARGIN);
  top = Math.min(Math.max(top, view.top + MARGIN), maxTop);

  popover.style.position = 'fixed';
  popover.style.inset = 'auto';
  popover.style.top = `${top}px`;
  popover.style.left = `${left}px`;
  popover.style.width = `${width}px`;
  popover.style.height = 'auto';
  popover.style.margin = '0';
  popover.style.zIndex = '120';
}

function clearPopover(popover: HTMLElement) {
  try {
    if (popover.matches(':popover-open')) popover.hidePopover();
  } catch {
    // The field can unmount while the calendar is open.
  }
  popover.removeAttribute('popover');
  popover.style.position = '';
  popover.style.inset = '';
  popover.style.top = '';
  popover.style.left = '';
  popover.style.width = '';
  popover.style.height = '';
  popover.style.margin = '';
  popover.style.maxHeight = '';
  popover.style.overflowY = '';
  popover.style.zIndex = '';
  popover.style.pointerEvents = '';
  popover.style.visibility = '';
}

function revealPopover(popover: HTMLElement) {
  if (typeof popover.showPopover !== 'function') return;
  popover.setAttribute('popover', 'manual');
  if (!popover.matches(':popover-open')) popover.showPopover();
}

/**
 * On desktop the calendar stays on the library's own position.
 * Tablet and mobile scroll containers clip that position, so once the opening
 * gesture is over we pin the calendar inside the viewport. It stays hidden
 * until that placement is done, so it does not flash under the field first.
 *
 * Focus runs on pointerdown. Moving the calendar before the click covers the
 * field on a short screen, the click lands on a day, and the library closes
 * at once. Wait for that click, and keep the calendar from taking it.
 */
export function useFloatingDatePopover(
  wrapperRef: RefObject<HTMLDivElement | null>,
  open: boolean,
) {
  useLayoutEffect(() => {
    if (!open || !isCompactViewport()) return undefined;
    trackPointers();
    const wrapper = wrapperRef.current;
    const popover = wrapper?.querySelector<HTMLElement>('.nice-dates-popover');
    const anchor = wrapper?.querySelector<HTMLElement>('input') ?? wrapper;
    if (!popover || !anchor) return undefined;

    popover.style.visibility = 'hidden';
    popover.style.pointerEvents = 'none';

    let frame = 0;
    let secondFrame = 0;
    let thirdFrame = 0;
    let fallback = 0;
    let revealed = false;

    const reveal = () => {
      if (revealed) return;
      revealed = true;
      window.clearTimeout(fallback);
      revealPopover(popover);
      placePopover(popover, anchor);
      popover.style.visibility = 'visible';
      popover.style.pointerEvents = 'auto';
      secondFrame = requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
        thirdFrame = requestAnimationFrame(() => placePopover(popover, anchor));
      });
    };

    if (pointerCount() > 0) {
      window.addEventListener('click', reveal, true);
      window.addEventListener('pointercancel', reveal, true);
      // A touch click can arrive after pointerup. Don't move before it.
      fallback = window.setTimeout(reveal, 500);
    } else {
      frame = requestAnimationFrame(reveal);
    }

    const place = () => {
      if (!revealed) return;
      if (!isCompactViewport()) {
        clearPopover(popover);
        return;
      }
      placePopover(popover, anchor);
    };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);

    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(secondFrame);
      cancelAnimationFrame(thirdFrame);
      window.clearTimeout(fallback);
      window.removeEventListener('click', reveal, true);
      window.removeEventListener('pointercancel', reveal, true);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      clearPopover(popover);
    };
  }, [open, wrapperRef]);
}
